
import { Dialog, DialogContent, MenuItem, TextField } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { initTransactionDialogData } from "./initData"
import { tyreApiNm, tyreTransactionColNm, trnsTypNm } from "./constant"
import { alertConfirmDelete } from "components/Alert"
import { bahtProps } from "utils/bahtProps"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"

const TyreTransactionDialog = ({ selectedId, tyrId, dialogOpen, setDialogOpen, onFinish }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initTransactionDialogData })
  const [origData, setOrigData] = useState({ ...initTransactionDialogData })
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])

  const dialogDataProp = useCallback((name) => ({
    fullWidth: true,
    size: "small",
    label: tyreTransactionColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const uploadFiles = useCallback(async (tyrTrnsId) => {
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      formData.append('TyrTrnsId', tyrTrnsId)
      formData.append('Label', pf.name)
      await ax.post(tyreApiNm.uploadTyreTransactionFile, formData)
    }
    setPendingFiles([])
  }, [ax, pendingFiles])

  const handleDialogOk = useCallback(async () => {
    if (selectedId) {
      const colValues = {}
      for (const key of ['TrnsTyp', 'TrnsDte', 'TukId', 'TlrId', 'TyrPos', 'Mileage', 'SupId', 'UntPr', 'DocNo', 'DocDte', 'Dscp', 'Rmk']) {
        if (String(dialogData[key] ?? '') !== String(origData[key] ?? '')) {
          colValues[key] = dialogData[key]
        }
      }
      const hasChange = Object.keys(colValues).length > 0
      const hasNewFiles = pendingFiles.length > 0
      if (hasChange) {
        await ax.post(tyreApiNm.updateTyreTransactionColumn, {
          TyrTrnsId: selectedId,
          ColValues: colValues,
        })
      }
      if (hasNewFiles) {
        await uploadFiles(selectedId)
      }
      if (hasChange || hasNewFiles) {
        onFinish()
      }
      setDialogOpen(false)
    } else {
      const postData = { ...dialogData, TyrId: tyrId }
      const value = await ax.post(tyreApiNm.insertTyreTransaction, postData)
      if (value.data) {
        const tyrTrnsId = value.data
        if (tyrTrnsId && pendingFiles.length > 0) {
          await uploadFiles(tyrTrnsId)
        }
        onFinish()
        setDialogOpen(false)
      }
    }
  }, [ax, dialogData, origData, onFinish, setDialogOpen, selectedId, tyrId, pendingFiles, uploadFiles])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(tyreApiNm.deleteTyreTransaction, { TyrTrnsId: selectedId }).then(value => {
        if (value.data) {
          onFinish()
          setDialogOpen(false)
        }
      })
    })
  }, [ax, onFinish, selectedId, setDialogOpen])

  const handleDeleteServerFile = useCallback((file) => {
    return new Promise((resolve) => {
      alertConfirmDelete(() => {
        ax.post(tyreApiNm.deleteTyreTransactionFile, { TyrTrnsFileId: file.TyrTrnsFileId }).then(value => {
          if (value.data) {
            resolve()
          }
        })
      })
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      if (selectedId) {
        ax.post(tyreApiNm.getTyreTransactionDetail, { TyrTrnsId: selectedId }).then(value => {
          if (value.data && value.data.length > 0) {
            setDialogData(value.data[0])
            setOrigData(value.data[0])
            setFilesData(value.data[0].Files || [])
          }
        })
      } else {
        setDialogData({ ...initTransactionDialogData, TyrId: tyrId })
        setFilesData([])
      }
    } else {
      setDialogData({ ...initTransactionDialogData })
      setOrigData({ ...initTransactionDialogData })
      setFilesData([])
      setPendingFiles(o => {
        o.forEach(f => URL.revokeObjectURL(f.preview))
        return []
      })
    }
  }, [dialogOpen, ax, selectedId, tyrId])

  const showVehicleFields = ['INSTALL', 'REMOVE', 'ROTATE'].includes(dialogData.TrnsTyp)
  const showSupplier = ['PURCHASE', 'RETREAD'].includes(dialogData.TrnsTyp)

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xs'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="รายการเคลื่อนไหวยาง"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <TextField {...dialogDataProp('TrnsTyp')} select required disabled={!!selectedId}>
            {Object.entries(trnsTypNm).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </TextField>
          <DesktopDatePicker
            label={tyreTransactionColNm.TrnsDte}
            inputFormat="DD/MM/YYYY"
            value={dialogData.TrnsDte}
            onChange={(v) => setDialogData(o => ({ ...o, TrnsDte: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
            renderInput={(params) => <TextField size="small" fullWidth {...params} />}
          />
          {showVehicleFields && (
            <>
              <ComboBox
                options={msData.truckCombo || []}
                label={tyreTransactionColNm.TukId}
                selectedId={dialogData.TukId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TukId: id }))}
              />
              <ComboBox
                options={msData.trailerCombo || []}
                label={tyreTransactionColNm.TlrId}
                selectedId={dialogData.TlrId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TlrId: id }))}
              />
              <TextField {...dialogDataProp('TyrPos')} />
            </>
          )}
          <TextField {...dialogDataProp('Mileage')} type="number" />
          {showSupplier && (
            <ComboBox
              options={msData.supplierCombo || []}
              label={tyreTransactionColNm.SupId}
              selectedId={dialogData.SupId}
              setSelectedId={(id) => setDialogData(o => ({ ...o, SupId: id }))}
            />
          )}
          {showSupplier && (
            <TextField {...dialogDataProp('UntPr')} type="number" {...bahtProps} />
          )}
          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('DocNo')} />
            <DesktopDatePicker
              label={tyreTransactionColNm.DocDte}
              inputFormat="DD/MM/YYYY"
              value={dialogData.DocDte}
              onChange={(v) => setDialogData(o => ({ ...o, DocDte: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
              renderInput={(params) => <TextField size="small" fullWidth {...params} />}
            />
          </BoxFR>
          <TextField {...dialogDataProp('Dscp')} multiline rows={2} />
          <TextField {...dialogDataProp('Rmk')} />
          <FilePreviewCard
            filesData={filesData}
            setFilesData={setFilesData}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            onDeleteServerFile={handleDeleteServerFile}
            fileIdKey="TyrTrnsFileId"
          />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(TyreTransactionDialog)
